/**
 * Configuration Google Analytics 4
 * Centralise la configuration et l'initialisation de Google Analytics
 */

import ReactGA from 'react-ga4';
import { getEnvironmentConfig, isDebugMode } from './environment-configuration';

/**
 * Initialise Google Analytics
 */
export function initGoogleAnalytics(): void {
  const config = getEnvironmentConfig();
  const measurementId = config.gaMeasurementId;
  
  // Ne pas initialiser si l'ID n'est pas configuré
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    console.log('📊 Google Analytics désactivé (ID non configuré)');
    return;
  }
  
  try {
    ReactGA.initialize(measurementId, {
      testMode: config.debug,
      gaOptions: {
        debug_mode: isDebugMode(),
      },
    });
    
    console.log(`✅ Google Analytics initialisé: ${measurementId}`);
  } catch (error) {
    console.error('❌ Erreur lors de l\'initialisation de Google Analytics:', error);
  }
}

/**
 * Envoie un événement de page view à Google Analytics
 */
export function trackPageView(path: string, title?: string): void {
  const config = getEnvironmentConfig();
  
  // Ne pas tracker si l'ID n'est pas configuré
  if (!config.gaMeasurementId || config.gaMeasurementId === 'G-XXXXXXXXXX') {
    return;
  }
  
  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      page_title: title,
    });
    
    if (isDebugMode()) {
      console.log(`📊 Page view tracked: ${path}`);
    }
  } catch (error) {
    console.error('❌ Erreur lors du tracking de page view:', error);
  }
}

/**
 * Envoie un événement personnalisé à Google Analytics
 */
export function trackEvent(
  category: string,
  action: string,
  label?: string,
  value?: number
): void {
  const config = getEnvironmentConfig();
  
  // Ne pas tracker si l'ID n'est pas configuré
  if (!config.gaMeasurementId || config.gaMeasurementId === 'G-XXXXXXXXXX') {
    return;
  }
  
  try {
    ReactGA.event({
      category,
      action,
      label,
      value,
    });
    
    if (isDebugMode()) {
      console.log(`📊 Event tracked: ${category} - ${action}`, { label, value });
    }
  } catch (error) {
    console.error('❌ Erreur lors du tracking d\'événement:', error);
  }
}

/**
 * Envoie un événement d'exception à Google Analytics
 * Note: GA4 utilise des événements personnalisés pour les exceptions
 */
export function trackException(description: string, fatal: boolean = false): void {
  const config = getEnvironmentConfig();
  
  // Ne pas tracker si l'ID n'est pas configuré
  if (!config.gaMeasurementId || config.gaMeasurementId === 'G-XXXXXXXXXX') {
    return;
  }
  
  try {
    // GA4 utilise des événements personnalisés pour les exceptions
    ReactGA.event('exception', {
      description,
      fatal: fatal ? 'true' : 'false',
    });
    
    if (isDebugMode()) {
      console.log(`📊 Exception tracked: ${description} (fatal: ${fatal})`);
    }
  } catch (error) {
    console.error('❌ Erreur lors du tracking d\'exception:', error);
  }
}

/**
 * Définit l'ID utilisateur pour le tracking
 */
export function setUserId(userId: string): void {
  const config = getEnvironmentConfig();
  
  // Ne pas définir si l'ID n'est pas configuré
  if (!config.gaMeasurementId || config.gaMeasurementId === 'G-XXXXXXXXXX') {
    return;
  }
  
  try {
    ReactGA.set({ userId });
    
    if (isDebugMode()) {
      console.log(`📊 User ID set: ${userId}`);
    }
  } catch (error) {
    console.error('❌ Erreur lors de la définition de l\'user ID:', error);
  }
}
